import React, { useState } from "react";
import { Link } from "react-router-dom";
import "./cms-legacy.css";

const API_BASE = "http://localhost:3001/api";

const TABS = [
  { id: "navigation", label: "Navigation" },
  { id: "home", label: "Hero" },
  { id: "projects", label: "Projects" },
  { id: "social", label: "Social Links" },
  { id: "about", label: "About" },
];

const CMS_PASSWORD = "cms2024";

const DEFAULT_PROJECT = { 
  title: "", 
  slug: "", 
  tagline: "", 
  description: "", 
  fullDescription: "",
  image: "", 
  images: [],
  videos: [],
  features: [],
  technologies: [],
  externalUrl: "", 
  githubUrl: "",
  status: "live"
};
const DEFAULT_ARTICLE = { title: "", date: "", url: "" };
const DEFAULT_VIDEO = { title: "", description: "", url: "", poster: "" };

export default function CMS() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [activeTab, setActiveTab] = useState("navigation");
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === CMS_PASSWORD) {
      setIsAuthenticated(true);
      loadData("navigation");
    } else {
      setMessage("Invalid password");
    }
  };

  const loadData = async (type) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/data/${type}`);
      const json = await res.json();
      setData((prev) => ({ ...prev, [type]: json }));
    } catch (err) {
      console.error("Failed to load data:", err);
    }
    setLoading(false);
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    if (!data[tabId]) {
      loadData(tabId);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage("");
    try {
      const res = await fetch(`${API_BASE}/data/${activeTab}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data[activeTab]),
      });
      if (res.ok) {
        setMessage("Saved successfully!");
      } else {
        setMessage("Failed to save");
      }
    } catch (err) {
      setMessage("Error: " + err.message);
    }
    setSaving(false);
  };

  const updateField = (field, value) => {
    setData((prev) => ({
      ...prev,
      [activeTab]: { ...prev[activeTab], [field]: value },
    }));
  };

  const updateArrayItem = (field, index, value) => {
    setData((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        [field]: prev[activeTab][field].map((item, i) =>
          i === index ? { ...item, ...value } : item
        ),
      },
    }));
  };

  const addArrayItem = (field, defaultItem) => {
    setData((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        [field]: [...(prev[activeTab][field] || []), defaultItem],
      },
    }));
  };

  const deleteArrayItem = (field, index) => {
    setData((prev) => ({
      ...prev,
      [activeTab]: {
        ...prev[activeTab],
        [field]: prev[activeTab][field].filter((_, i) => i !== index),
      },
    }));
  };

  const renderEditor = () => {
    const content = data[activeTab];
    if (!content) return null;

    switch (activeTab) {
      case "navigation":
        return (
          <div className="cms-section">
            <h3 className="text-xl font-bold mb-4">Navigation Links</h3>
            <p className="text-gray-600 mb-4">Edit the navigation menu items.</p>
            {content.items?.map((item, index) => (
              <div key={index} className="flex gap-4 mb-4 items-center">
                <input
                  type="text"
                  value={item.label}
                  onChange={(e) =>
                    updateArrayItem("items", index, { label: e.target.value })
                  }
                  placeholder="Label"
                  className="border p-2 flex-1 rounded"
                />
                <input
                  type="text"
                  value={item.href}
                  onChange={(e) =>
                    updateArrayItem("items", index, { href: e.target.value })
                  }
                  placeholder="href (e.g., #about)"
                  className="border p-2 flex-1 rounded"
                />
                <button
                  onClick={() => deleteArrayItem("items", index)}
                  className="text-red-500 hover:text-red-700 px-2"
                >
                  Delete
                </button>
              </div>
            ))}
          </div>
        );

      case "home":
        return (
          <div className="cms-section">
            <h3 className="text-xl font-bold mb-4">Hero Section</h3>
            <div className="space-y-4">
              <div>
                <label className="block mb-2 font-medium">Hero Title</label>
                <input
                  type="text"
                  value={content.heroTitle || ""}
                  onChange={(e) => updateField("heroTitle", e.target.value)}
                  className="border p-2 w-full rounded"
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">Hero Subtitle</label>
                <textarea
                  type="text"
                  value={content.heroSubtitle || ""}
                  onChange={(e) => updateField("heroSubtitle", e.target.value)}
                  className="border p-2 w-full rounded h-24"
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">CTA Button Text</label>
                <input
                  type="text"
                  value={content.ctaText || ""}
                  onChange={(e) => updateField("ctaText", e.target.value)}
                  className="border p-2 w-full rounded"
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">Video URL</label>
                <input
                  type="text"
                  value={content.videoUrl || ""}
                  onChange={(e) => updateField("videoUrl", e.target.value)}
                  className="border p-2 w-full rounded"
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">Fallback Image</label>
                <input
                  type="text"
                  value={content.fallbackImage || ""}
                  onChange={(e) => updateField("fallbackImage", e.target.value)}
                  className="border p-2 w-full rounded"
                />
              </div>
            </div>
          </div>
        );

      case "projects":
        return (
          <div className="cms-section">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-xl font-bold">Portfolio Projects</h3>
                <p className="text-gray-600 text-sm">Edit your portfolio projects.</p>
              </div>
              <button
                onClick={() => addArrayItem("projects", DEFAULT_PROJECT)}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
              >
                + Add Project
              </button>
            </div>
            {content.projects?.map((project, index) => (
              <div
                key={index}
                className="border p-4 mb-6 rounded bg-gray-50"
              >
                <div className="flex justify-between items-start mb-4">
                  <span className="font-medium text-gray-700">Project {index + 1}: {project.title || 'Untitled'}</span>
                  <button
                    onClick={() => deleteArrayItem("projects", index)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    Delete
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block mb-1 text-sm font-medium">Title</label>
                    <input
                      type="text"
                      value={project.title || ""}
                      onChange={(e) =>
                        updateArrayItem("projects", index, {
                          title: e.target.value,
                        })
                      }
                      className="border p-2 w-full rounded"
                    />
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-medium">Slug (URL)</label>
                    <input
                      type="text"
                      value={project.slug || ""}
                      onChange={(e) =>
                        updateArrayItem("projects", index, {
                          slug: e.target.value,
                        })
                      }
                      placeholder="my-project"
                      className="border p-2 w-full rounded"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block mb-1 text-sm font-medium">Tagline</label>
                  <input
                    type="text"
                    value={project.tagline || ""}
                    onChange={(e) =>
                      updateArrayItem("projects", index, {
                        tagline: e.target.value,
                      })
                    }
                    placeholder="Brief tagline for the project"
                    className="border p-2 w-full rounded"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block mb-1 text-sm font-medium">Card Description (short)</label>
                    <textarea
                      value={project.description || ""}
                      onChange={(e) =>
                        updateArrayItem("projects", index, {
                          description: e.target.value,
                        })
                      }
                      className="border p-2 w-full rounded h-20"
                    />
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-medium">Full Description</label>
                    <textarea
                      value={project.fullDescription || ""}
                      onChange={(e) =>
                        updateArrayItem("projects", index, {
                          fullDescription: e.target.value,
                        })
                      }
                      placeholder="Detailed description (use blank lines for paragraphs)"
                      className="border p-2 w-full rounded h-20"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div>
                    <label className="block mb-1 text-sm font-medium">Cover Image URL</label>
                    <input
                      type="text"
                      value={project.image || ""}
                      onChange={(e) =>
                        updateArrayItem("projects", index, {
                          image: e.target.value,
                        })
                      }
                      className="border p-2 w-full rounded"
                    />
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-medium">Live URL</label>
                    <input
                      type="text"
                      value={project.externalUrl || ""}
                      onChange={(e) =>
                        updateArrayItem("projects", index, {
                          externalUrl: e.target.value,
                        })
                      }
                      className="border p-2 w-full rounded"
                    />
                  </div>
                  <div>
                    <label className="block mb-1 text-sm font-medium">GitHub URL</label>
                    <input
                      type="text"
                      value={project.githubUrl || ""}
                      onChange={(e) =>
                        updateArrayItem("projects", index, {
                          githubUrl: e.target.value,
                        })
                      }
                      className="border p-2 w-full rounded"
                    />
                  </div>
                </div>

                <div className="mb-4">
                  <label className="block mb-1 text-sm font-medium">Status</label>
                  <select
                    value={project.status || "live"}
                    onChange={(e) =>
                      updateArrayItem("projects", index, {
                        status: e.target.value,
                      })
                    }
                    className="border p-2 w-full rounded"
                  >
                    <option value="live">Live</option>
                    <option value="archived">Archived</option>
                    <option value="wip">Work in Progress</option>
                  </select>
                </div>

                <div className="border-t pt-4 mt-4">
                  <label className="block mb-2 text-sm font-medium">Technologies</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {(project.technologies || []).map((tech, tIndex) => (
                      <span key={tIndex} className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-sm flex items-center gap-1">
                        {tech}
                        <button
                          onClick={() => {
                            const newTechs = [...(project.technologies || [])];
                            newTechs.splice(tIndex, 1);
                            updateArrayItem("projects", index, { technologies: newTechs });
                          }}
                          className="hover:text-red-700"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      id={`tech-${index}`}
                      placeholder="Add technology"
                      className="border p-2 flex-1 rounded"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.target.value.trim()) {
                          e.preventDefault();
                          const newTechs = [...(project.technologies || []), e.target.value.trim()];
                          updateArrayItem("projects", index, { technologies: newTechs });
                          e.target.value = '';
                        }
                      }}
                    />
                    <button
                      onClick={() => {
                        const input = document.getElementById(`tech-${index}`);
                        if (input.value.trim()) {
                          const newTechs = [...(project.technologies || []), input.value.trim()];
                          updateArrayItem("projects", index, { technologies: newTechs });
                          input.value = '';
                        }
                      }}
                      className="bg-gray-200 px-4 rounded hover:bg-gray-300"
                    >
                      Add
                    </button>
                  </div>
                </div>

                <div className="border-t pt-4 mt-4">
                  <label className="block mb-2 text-sm font-medium">Features</label>
                  <div className="space-y-2 mb-2">
                    {(project.features || []).map((feature, fIndex) => (
                      <div key={fIndex} className="flex gap-2 items-center">
                        <span className="flex-1 bg-gray-100 px-2 py-1 rounded text-sm">{feature}</span>
                        <button
                          onClick={() => {
                            const newFeatures = [...(project.features || [])];
                            newFeatures.splice(fIndex, 1);
                            updateArrayItem("projects", index, { features: newFeatures });
                          }}
                          className="text-red-500 hover:text-red-700"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      id={`feature-${index}`}
                      placeholder="Add feature"
                      className="border p-2 flex-1 rounded"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && e.target.value.trim()) {
                          e.preventDefault();
                          const newFeatures = [...(project.features || []), e.target.value.trim()];
                          updateArrayItem("projects", index, { features: newFeatures });
                          e.target.value = '';
                        }
                      }}
                    />
                    <button
                      onClick={() => {
                        const input = document.getElementById(`feature-${index}`);
                        if (input.value.trim()) {
                          const newFeatures = [...(project.features || []), input.value.trim()];
                          updateArrayItem("projects", index, { features: newFeatures });
                          input.value = '';
                        }
                      }}
                      className="bg-gray-200 px-4 rounded hover:bg-gray-300"
                    >
                      Add
                    </button>
                  </div>
                </div>

                <div className="border-t pt-4 mt-4">
                  <label className="block mb-2 text-sm font-medium">Gallery Images</label>
                  <div className="space-y-2 mb-2">
                    {(project.images || []).map((img, imgIndex) => (
                      <div key={imgIndex} className="flex gap-2 items-center">
                        <input
                          type="text"
                          value={img}
                          onChange={(e) => {
                            const newImages = [...(project.images || [])];
                            newImages[imgIndex] = e.target.value;
                            updateArrayItem("projects", index, { images: newImages });
                          }}
                          className="border p-2 flex-1 rounded text-sm"
                          placeholder="Image URL"
                        />
                        <button
                          onClick={() => {
                            const newImages = [...(project.images || [])];
                            newImages.splice(imgIndex, 1);
                            updateArrayItem("projects", index, { images: newImages });
                          }}
                          className="text-red-500 hover:text-red-700"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => {
                      const newImages = [...(project.images || []), ""];
                      updateArrayItem("projects", index, { images: newImages });
                    }}
                    className="bg-gray-200 px-4 py-2 rounded text-sm hover:bg-gray-300"
                  >
                    + Add Image
                  </button>
                </div>

                <div className="border-t pt-4 mt-4">
                  <label className="block mb-2 text-sm font-medium">Videos</label>
                  <div className="space-y-3 mb-2">
                    {(project.videos || []).map((vid, vidIndex) => (
                      <div key={vidIndex} className="bg-white p-3 rounded border">
                        <div className="flex justify-between mb-2">
                          <span className="text-sm font-medium">Video {vidIndex + 1}</span>
                          <button
                            onClick={() => {
                              const newVideos = [...(project.videos || [])];
                              newVideos.splice(vidIndex, 1);
                              updateArrayItem("projects", index, { videos: newVideos });
                            }}
                            className="text-red-500 hover:text-red-700 text-sm"
                          >
                            Remove
                          </button>
                        </div>
                        <input
                          type="text"
                          value={vid.title || ""}
                          onChange={(e) => {
                            const newVideos = [...(project.videos || [])];
                            newVideos[vidIndex] = { ...newVideos[vidIndex], title: e.target.value };
                            updateArrayItem("projects", index, { videos: newVideos });
                          }}
                          className="border p-2 w-full rounded text-sm mb-2"
                          placeholder="Video title"
                        />
                        <input
                          type="text"
                          value={vid.url || ""}
                          onChange={(e) => {
                            const newVideos = [...(project.videos || [])];
                            newVideos[vidIndex] = { ...newVideos[vidIndex], url: e.target.value };
                            updateArrayItem("projects", index, { videos: newVideos });
                          }}
                          className="border p-2 w-full rounded text-sm"
                          placeholder="YouTube or video URL"
                        />
                      </div>
                    ))}
                  </div>
                  <button
                    onClick={() => {
                      const newVideos = [...(project.videos || []), { title: "", url: "" }];
                      updateArrayItem("projects", index, { videos: newVideos });
                    }}
                    className="bg-gray-200 px-4 py-2 rounded text-sm hover:bg-gray-300"
                  >
                    + Add Video
                  </button>
                </div>
              </div>
            ))}
          </div>
        );

      case "social":
        return (
          <div className="cms-section">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-xl font-bold">Social Links</h3>
                <p className="text-gray-600 text-sm">Edit your social media links.</p>
              </div>
              <button
                onClick={() => addArrayItem("socialLinks", { name: "", url: "", icon: "" })}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
              >
                + Add Link
              </button>
            </div>
            {content.socialLinks?.map((link, index) => (
              <div key={index} className="border p-3 mb-3 rounded bg-gray-50">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-medium text-gray-700">Link {index + 1}</span>
                  <button
                    onClick={() => deleteArrayItem("socialLinks", index)}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    Delete
                  </button>
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={link.name}
                    onChange={(e) =>
                      updateArrayItem("socialLinks", index, {
                        name: e.target.value,
                      })
                    }
                    placeholder="Name"
                    className="border p-2 w-1/4 rounded"
                  />
                  <input
                    type="text"
                    value={link.url}
                    onChange={(e) =>
                      updateArrayItem("socialLinks", index, {
                        url: e.target.value,
                      })
                    }
                    placeholder="URL"
                    className="border p-2 flex-1 rounded"
                  />
                  <input
                    type="text"
                    value={link.icon}
                    onChange={(e) =>
                      updateArrayItem("socialLinks", index, {
                        icon: e.target.value,
                      })
                    }
                    placeholder="Icon name"
                    className="border p-2 w-1/4 rounded"
                  />
                </div>
              </div>
            ))}
          </div>
        );

      case "about":
        return (
          <div className="cms-section">
            <h3 className="text-xl font-bold mb-4">About Section</h3>
            <div className="space-y-4">
              <div>
                <label className="block mb-2 font-medium">Bio</label>
                <textarea
                  value={content.bio || ""}
                  onChange={(e) => updateField("bio", e.target.value)}
                  className="border p-2 w-full rounded h-32"
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">Current Work</label>
                <textarea
                  value={content.currentWork || ""}
                  onChange={(e) => updateField("currentWork", e.target.value)}
                  className="border p-2 w-full rounded h-24"
                />
              </div>
              <div>
                <label className="block mb-2 font-medium">AI Interest</label>
                <textarea
                  value={content.aiInterest || ""}
                  onChange={(e) => updateField("aiInterest", e.target.value)}
                  className="border p-2 w-full rounded h-24"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block mb-2 font-medium">Substack URL</label>
                  <input
                    type="text"
                    value={content.substackUrl || ""}
                    onChange={(e) => updateField("substackUrl", e.target.value)}
                    className="border p-2 w-full rounded"
                  />
                </div>
                <div>
                  <label className="block mb-2 font-medium">Medium URL</label>
                  <input
                    type="text"
                    value={content.mediumUrl || ""}
                    onChange={(e) => updateField("mediumUrl", e.target.value)}
                    className="border p-2 w-full rounded"
                  />
                </div>
              </div>

              <div className="mt-6 pt-4 border-t">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-lg font-bold">Featured Articles</h4>
                  <button
                    onClick={() => addArrayItem("articles", DEFAULT_ARTICLE)}
                    className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                  >
                    + Add Article
                  </button>
                </div>
                {content.articles?.map((article, index) => (
                  <div key={index} className="border p-3 mb-3 rounded bg-gray-50">
                    <div className="flex justify-between items-center mb-2">
                      <span className="font-medium text-gray-700">Article {index + 1}</span>
                      <button
                        onClick={() => deleteArrayItem("articles", index)}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        Delete
                      </button>
                    </div>
                    <div className="mb-2">
                      <input
                        type="text"
                        value={article.title}
                        onChange={(e) =>
                          updateArrayItem("articles", index, {
                            title: e.target.value,
                          })
                        }
                        placeholder="Article Title"
                        className="border p-2 w-full rounded"
                      />
                    </div>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={article.date}
                        onChange={(e) =>
                          updateArrayItem("articles", index, {
                            date: e.target.value,
                          })
                        }
                        placeholder="Date"
                        className="border p-2 w-1/3 rounded"
                      />
                      <input
                        type="text"
                        value={article.url}
                        onChange={(e) =>
                          updateArrayItem("articles", index, {
                            url: e.target.value,
                          })
                        }
                        placeholder="Article URL"
                        className="border p-2 flex-1 rounded"
                      />
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-6 pt-4 border-t">
                <div className="flex justify-between items-center mb-3">
                  <h4 className="text-lg font-bold">Videos</h4>
                  <button
                    onClick={() => addArrayItem("videos", DEFAULT_VIDEO)}
                    className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700"
                  >
                    + Add Video
                  </button>
                </div>
                {content.videos?.map((video, index) => (
                  <div key={index} className="border p-4 mb-3 rounded bg-gray-50">
                    <div className="flex justify-between items-center mb-3">
                      <span className="font-medium text-gray-700">Video {index + 1}</span>
                      <button
                        onClick={() => deleteArrayItem("videos", index)}
                        className="text-red-500 hover:text-red-700 text-sm"
                      >
                        Delete
                      </button>
                    </div>
                    <div className="mb-3">
                      <label className="block mb-1 text-sm font-medium">Title</label>
                      <input
                        type="text"
                        value={video.title}
                        onChange={(e) =>
                          updateArrayItem("videos", index, {
                            title: e.target.value,
                          })
                        }
                        placeholder="Video Title"
                        className="border p-2 w-full rounded"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="block mb-1 text-sm font-medium">Description</label>
                      <textarea
                        value={video.description}
                        onChange={(e) =>
                          updateArrayItem("videos", index, {
                            description: e.target.value,
                          })
                        }
                        placeholder="Video Description"
                        className="border p-2 w-full rounded h-16"
                      />
                    </div>
                    <div className="mb-3">
                      <label className="block mb-1 text-sm font-medium">Video URL</label>
                      <input
                        type="text"
                        value={video.url}
                        onChange={(e) =>
                          updateArrayItem("videos", index, {
                            url: e.target.value,
                          })
                        }
                        placeholder="https://..."
                        className="border p-2 w-full rounded"
                      />
                    </div>
                    <div>
                      <label className="block mb-1 text-sm font-medium">Poster Image URL</label>
                      <input
                        type="text"
                        value={video.poster || ""}
                        onChange={(e) =>
                          updateArrayItem("videos", index, {
                            poster: e.target.value,
                          })
                        }
                        placeholder="https://..."
                        className="border p-2 w-full rounded"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="cms-root min-h-screen flex items-center justify-center bg-gray-100">
        <div className="bg-white p-8 rounded-lg shadow-md w-96">
          <h1 className="text-2xl font-bold mb-6 text-center">CMS Dashboard</h1>
          <form onSubmit={handleLogin}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="border p-3 w-full mb-4 rounded"
            />
            {message && <p className="text-red-500 mb-4">{message}</p>}
            <button
              type="submit"
              className="bg-black text-white p-3 w-full rounded hover:bg-gray-800"
            >
              Login
            </button>
          </form>
          <Link
            to="/"
            className="mt-4 text-gray-600 hover:underline w-full block text-center"
          >
            Back to site
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="cms-root min-h-screen bg-gray-100">
      <header className="bg-black text-white p-4">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <h1 className="text-xl font-bold">CMS Dashboard</h1>
          <div className="flex gap-4 items-center">
            <Link to="/" className="text-sm hover:underline">
              View Site
            </Link>
            <button
              onClick={() => setIsAuthenticated(false)}
              className="text-sm hover:underline"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto p-6">
        <div className="flex gap-1 mb-6 border-b border-gray-300">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => handleTabChange(tab.id)}
              className={`px-4 py-3 font-medium transition-colors ${
                activeTab === tab.id
                  ? "border-b-2 border-black text-black"
                  : "text-gray-500 hover:text-black"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-10 text-gray-500">Loading...</div>
        ) : (
          <div className="bg-white p-6 rounded-lg shadow">
            {renderEditor()}

            <div className="mt-6 flex gap-4 items-center pt-4 border-t">
              <button
                onClick={handleSave}
                disabled={saving}
                className="bg-black text-white px-6 py-2 rounded hover:bg-gray-800 disabled:opacity-50"
              >
                {saving ? "Saving..." : "Save Changes"}
              </button>
              {message && (
                <span
                  className={
                    message.includes("success")
                      ? "text-green-600"
                      : "text-red-600"
                  }
                >
                  {message}
                </span>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
